import { WorkflowStep, WorkflowStepGroup } from "../../../__generated__/api";
import { ID_DELIMITER, SUBACTION_SUFFIX } from "./build-flow";

interface FindNodeAndPushCardOptions {
    step: WorkflowStep | WorkflowStepGroup;
    newCard: WorkflowStep;
    newCardPartNodeId: string;
    currentStepParentId: string;
    index?: number;
}

const isMatchingParentId = (currentStepParentId: string, newCardParentId: string) => currentStepParentId === newCardParentId
    || `${currentStepParentId}${SUBACTION_SUFFIX}` === newCardParentId;

export const findNodeAndPushCard = ({
    step, newCard, newCardPartNodeId, currentStepParentId, index,
}: FindNodeAndPushCardOptions) => {
    const [childId, parentId] = newCardPartNodeId.split(ID_DELIMITER);
    if (step.__typename === "WorkflowStepGroup") {
        if (childId === step.groupId && isMatchingParentId(currentStepParentId, parentId)) {
            if (index !== undefined) {
                step.children?.splice(index, 0, newCard);
            } else {
                step.children?.push(newCard);
            }
        } else {
            step.children?.forEach(childStep => findNodeAndPushCard({
                step: childStep,
                newCard,
                newCardPartNodeId,
                currentStepParentId: step.groupId,
                index,
            }));
        }
    } else if (step.__typename === "WorkflowStep") {
        const childrenMap = new Map<string, WorkflowStepGroup>();
        step.children?.forEach(child => {
            childrenMap.set(child.groupId, child);
        });

        step.children?.forEach(childStep => findNodeAndPushCard({
            step: childStep,
            newCard,
            newCardPartNodeId,
            currentStepParentId: step.id,
            index,
        }));

        const filteredAllowedChildren = step.action?.allowedChildren
            ?.filter(child => !childrenMap.has(child.id));

        if (filteredAllowedChildren) {
            const allowedChildParentId = step.id;
            filteredAllowedChildren.forEach(child => {
                if (childId === child.id && isMatchingParentId(allowedChildParentId, parentId)) {
                    const newWorkflowGroupStep: WorkflowStepGroup = {
                        __typename: "WorkflowStepGroup" as const,
                        groupId: child.id,
                        children: [newCard],
                    };

                    const existingChildren = step.children ?? [];
                    existingChildren.push(newWorkflowGroupStep);
                    // eslint-disable-next-line no-param-reassign
                    step.children = existingChildren;
                }
            });
        }
    }
};

export const findNodeAndRemoveCard = (step: WorkflowStep | WorkflowStepGroup, cardId: string) => {
    if (step.__typename === "WorkflowStepGroup") {
        step.children?.forEach((childStep, idx) => {
            if (childStep.id === cardId) {
                step.children?.splice(idx, 1);
            } else {
                findNodeAndRemoveCard(childStep, cardId);
            }
        });
    } else if (step.__typename === "WorkflowStep") {
        step.children?.forEach(childStep => findNodeAndRemoveCard(childStep, cardId));
    }
};

export const findNodeAndReplaceCard = (
    step: WorkflowStep | WorkflowStepGroup,
    cardId: string,
    replacement: WorkflowStep,
) => {
    if (step.__typename === "WorkflowStepGroup") {
        step?.children?.forEach((childStep, idx) => {
            if (childStep.id === cardId) {
                const replacementWithChildren = {
                    ...replacement,
                    children: childStep.children,
                };

                step.children?.splice(idx, 1, replacementWithChildren);
            } else {
                findNodeAndReplaceCard(childStep, cardId, replacement);
            }
        });
    } else if (step.__typename === "WorkflowStep") {
        step.children?.forEach(childStep => findNodeAndReplaceCard(childStep, cardId, replacement));
    }
};

export const findNodeAndReorderCards = (
    step: WorkflowStep | WorkflowStepGroup,
    sortedNodes: WorkflowStep[],
    parentNodeId: string,
) => {
    if (step.__typename === "WorkflowStepGroup") {
        if (parentNodeId.includes(step.groupId)) {
            step.children?.splice(0, step.children.length, ...sortedNodes);
        } else {
            step.children?.forEach(childStep => findNodeAndReorderCards(childStep, sortedNodes, parentNodeId));
        }
    } else if (step.__typename === "WorkflowStep") {
        step.children?.forEach(childStep => findNodeAndReorderCards(childStep, sortedNodes, parentNodeId));
    }
};
